import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Copy, RefreshCw, Lock } from "lucide-react";
import { PasswordField } from "@/components/PasswordField";
import { passwordStrength } from "@/lib/password-strength";
import {
  disableTotp,
  enrollTotp,
  getMyTotpStatus,
  regenerateBackupCodes,
  verifyTotpEnrollment,
} from "@/lib/totp.functions";
import { renderTotpQr } from "@/lib/totp-qr.functions";
import { changePassword } from "@/lib/password.functions";

export function SecuritySection() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const [setupOpen, setSetupOpen] = useState(false);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string>("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showBackup, setShowBackup] = useState<string[] | null>(null);

  async function refresh() {
    try {
      const s = await getMyTotpStatus();
      setEnabled(s.enrolled);
    } catch {
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function startSetup() {
    setSubmitting(true);
    try {
      const { otpauthUri, secret } = await enrollTotp();
      setOtpauthUri(otpauthUri);
      setSecret(secret);
      setSetupOpen(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start enrollment");
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmSetup() {
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your authenticator");
      return;
    }
    setSubmitting(true);
    try {
      const { backupCodes } = await verifyTotpEnrollment({ data: { code } });
      setSetupOpen(false);
      setOtpauthUri(null);
      setSecret("");
      setCode("");
      setShowBackup(backupCodes);
      setEnabled(true);
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function disable2FA() {
    if (!confirm("Disable two-factor authentication?")) return;
    setSubmitting(true);
    try {
      await disableTotp();
      setEnabled(false);
      toast.success("Two-factor authentication disabled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not disable 2FA");
    } finally {
      setSubmitting(false);
    }
  }

  async function regen() {
    setSubmitting(true);
    try {
      const { backupCodes } = await regenerateBackupCodes();
      setShowBackup(backupCodes);
      toast.success("Backup codes regenerated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not regenerate codes");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="security" className="panel space-y-5 p-5 scroll-mt-20">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Security
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Two-factor authentication and password.</p>
        </div>
        {enabled && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-500">
            <ShieldCheck className="h-3 w-3" /> 2FA Enabled
          </span>
        )}
      </div>

      <div className="rounded-md border border-border p-4">
        <h3 className="text-sm font-semibold">Two-Factor Authentication</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Add a second verification step on sign-in using an authenticator app like Google Authenticator, Authy, or 1Password.
        </p>

        {loading ? (
          <p className="mt-3 text-xs text-muted-foreground">Loading…</p>
        ) : enabled ? (
          <div className="mt-3 space-y-2 text-sm">
            <p>Method: <span className="font-semibold">Authenticator App</span></p>
            <div className="flex flex-wrap gap-2">
              <button disabled={submitting} onClick={regen} className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
                <RefreshCw className="h-3 w-3" /> Regenerate backup codes
              </button>
              <button disabled={submitting} onClick={disable2FA} className="rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60">
                Disable 2FA
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <button
              disabled={submitting}
              onClick={startSetup}
              className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3 text-left hover:border-primary disabled:opacity-60"
            >
              <KeyRound className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <div className="text-sm font-semibold">Set up Authenticator App</div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">A unique secret is generated for your account on the server.</p>
              </div>
            </button>
          </div>
        )}
      </div>

      <ChangePasswordCard twoFactorEnabled={enabled} />

      {setupOpen && otpauthUri && (
        <Modal onClose={() => { setSetupOpen(false); setCode(""); setOtpauthUri(null); }}>
          <h3 className="font-display text-lg font-bold">Set up Authenticator App</h3>
          <div className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground">Scan this QR code with your authenticator app, then enter the 6-digit code below.</p>
            <div className="grid place-items-center rounded-md border border-border bg-white p-4">
              <QrSvg text={otpauthUri} />
            </div>
            <p className="text-center font-mono text-[11px] text-muted-foreground break-all">
              Or enter key manually: <span className="text-foreground">{secret}</span>
            </p>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">6-digit code</span>
              <input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-center text-lg tracking-[0.5em]"
              />
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => { setSetupOpen(false); setCode(""); setOtpauthUri(null); }} className="btn-ghost px-3.5 py-2 text-sm">Cancel</button>
            <button disabled={submitting} onClick={confirmSetup} className="btn-primary px-3.5 py-2 text-sm disabled:opacity-60">
              {submitting ? "Verifying…" : "Confirm & enable"}
            </button>
          </div>
        </Modal>
      )}

      {showBackup && (
        <Modal onClose={() => setShowBackup(null)}>
          <h3 className="font-display text-lg font-bold">Your backup codes</h3>
          <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
            Save these codes somewhere safe — they're shown only once. Use one if you lose access to your authenticator device.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
            {showBackup.map((c) => (
              <div key={c} className="rounded-md border border-border bg-background/40 px-3 py-2 text-center">{c}</div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => {
                navigator.clipboard?.writeText(showBackup.join("\n"));
                toast.success("Copied to clipboard");
              }}
              className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
            >
              <Copy className="h-3 w-3" /> Copy all
            </button>
            <button onClick={() => setShowBackup(null)} className="btn-primary px-3.5 py-2 text-sm">
              I've saved them
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function ChangePasswordCard({ twoFactorEnabled }: { twoFactorEnabled: boolean }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [twofa, setTwofa] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { allPassed } = useMemo(() => passwordStrength(next), [next]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!current) return toast.error("Enter your current password");
    if (!allPassed) return toast.error("New password does not meet all requirements");
    if (twoFactorEnabled && twofa.trim().length === 0) {
      return toast.error("Enter your 2FA code or a backup code");
    }

    setSubmitting(true);
    try {
      const res = await changePassword({
        data: {
          current_password: current,
          new_password: next,
          totp_code: twoFactorEnabled ? twofa.trim() : undefined,
        },
      });
      if (!res.ok) throw new Error(res.error);
      setCurrent(""); setNext(""); setTwofa("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-border p-4">
      <h3 className="text-sm font-semibold flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Change password</h3>
      <label className="block">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current password</span>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
        />
      </label>
      <PasswordField label="New password" value={next} onChange={setNext} />
      {twoFactorEnabled && (
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">2FA code or backup code</span>
          <input
            inputMode="text"
            maxLength={12}
            value={twofa}
            onChange={(e) => setTwofa(e.target.value)}
            placeholder="123456 or XXXX-XXXX"
            className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm tracking-widest"
          />
        </label>
      )}
      <div className="flex justify-end">
        <button type="submit" disabled={submitting} className="btn-primary px-4 py-2 text-sm disabled:opacity-60">
          {submitting ? "Updating…" : "Update password"}
        </button>
      </div>
    </form>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-border bg-panel p-6 shadow-2xl">
        {children}
      </div>
    </div>
  );
}

// QR rendered server-side so the otpauth URI (which contains the TOTP
// secret) never leaves our infrastructure.
function QrSvg({ text }: { text: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    renderTotpQr({ data: { uri: text } })
      .then((r) => { if (!cancelled) setSvg(r.svg); })
      .catch(() => { if (!cancelled) setSvg(null); });
    return () => { cancelled = true; };
  }, [text]);
  if (!svg) {
    return <div className="h-[180px] w-[180px] animate-pulse rounded bg-muted" />;
  }
  return <div className="h-[180px] w-[180px]" dangerouslySetInnerHTML={{ __html: svg }} />;
}
